import { cn } from "@/lib/utils";
import { Eyebrow, Title } from "./title";
import type { ReactNode } from "react";

type Tone = "default" | "inverse";
type Align = "left" | "center";
type Variant = "hero" | "section" | "sub";

type Props = {
  eyebrow?: ReactNode;
  title: ReactNode;
  accent?: string;
  description?: ReactNode;
  variant?: Variant;
  as?: "h1" | "h2" | "h3";
  tone?: Tone;
  align?: Align;
  divider?: boolean;
  className?: string;
};

export function SectionHeader({
  eyebrow,
  title,
  accent,
  description,
  variant = "section",
  as,
  tone = "default",
  align = "left",
  divider = false,
  className,
}: Props) {
  return (
    <div
      className={cn(
        "flex flex-col",
        align === "center" && "items-center text-center",
        className,
      )}
    >
      {eyebrow && (
        <Eyebrow tone={tone} align={align} className="mb-5">
          {eyebrow}
        </Eyebrow>
      )}

      <Title
        variant={variant}
        as={as}
        accent={accent}
        tone={tone}
        align={align}
        className={cn(
          variant !== "sub" && "max-w-[20ch]",
          align === "center" && "max-w-[24ch] mx-auto",
        )}
      >
        {title}
      </Title>

      {divider && (
        <span
          aria-hidden
          className={cn(
            "block h-px w-16 mt-7",
            tone === "inverse" ? "bg-background/45" : "bg-brand-800/45",
          )}
        />
      )}

      {description && (
        <p
          className={cn(
            "mt-5 leading-[1.65] tracking-[-0.005em] text-[15px] md:text-base max-w-[560px]",
            align === "center" && "mx-auto",
            tone === "inverse" ? "text-background/80" : "text-foreground/65",
          )}
        >
          {description}
        </p>
      )}
    </div>
  );
}
